import React, { useMemo, useState } from 'react';
import { Image, message } from 'antd';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { getScheduleReportByJobIdQuery } from '@src/util/query/scheduleReport.query';
import { getScheduledJobById } from '@src/util/query/schedule.query';
import { getMobileResponseByIdQuery } from '@src/util/query/mobileResponse.query';
import Spinner from "@src/components/spinner";
import { downloadReportPdf } from '@src/util/reportPdf/downloadReportPdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { getReportTemplateById } from "@src/util/query/reportTemplate.query";
import { getDutyProofQuery } from '@src/util/query/dutyProof.query';
import { FileFormatTypes } from '@src/util/fileFormatTypes';
import {
    mapReportDetailToFieldState, isFieldVisible, legacyKeyForField, normalizeYesNo,
} from '@src/util/reportFieldAnswers/reportFieldAnswers';
import './genericMobileReport.css';

// The fallback layout for every report template that has no hand-built design of its own
// (see util/mobileDutyReportToTemplateMapping). Laid out like the proof of attendance /
// duty report: header band, who completed it, summary cards, then each section of the
// form as one QUESTION | ANSWER table, photos in the answer cell of their own question. The
// downloaded PDF is the same layout drawn by wfc-server (GET /reports/form/:jobId/pdf,
// utils/pdf/formReportPdf.ts) - keep the two in step.
//
// It knows nothing about any particular template: it walks the template's own sections
// and fields, works out how to show each answer from its fieldType and, failing that,
// from the value itself, and lists any answer the template doesn't describe (legacy
// templates, fields since removed) under "Additional details" rather than dropping it.

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'jfif', 'svg']);
// answers to these are typed by a person - never reinterpret them as a file or date
const TEXT_TYPES = new Set(['singlelineText', 'multilineText', 'email', 'number', 'password']);
const MULTI_TYPES = new Set(['multiCheckbox', 'multicheckbox']);
const ISO_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const UK_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ID_RE = /^([0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const PHOTO_SUFFIX_RE = /_photo(_?\d+)?$/;
// written by the app into every report - already in the header
const HEADER_KEYS = new Set(['sitename', 'siteaddress']);

// legacy answers are keyed by the label with spaces removed, so the words are gone -
// the common ones the app writes itself get their proper names back
const KNOWN_KEYS = {
    sitename: 'Site name',
    siteaddress: 'Site address',
    jobtype: 'Job type',
    timein: 'Time in',
    timeout: 'Time out',
    onsitetime: 'On site time',
    offsitetime: 'Off site time',
    "officer'scomments": "Officer's comments",
    officername: 'Officer name',
    date: 'Date',
    notes: 'Notes',
};

// default.css lifts every modal and drawer to z-index 9999 (!important) - this report is
// shown inside those (Task Management modal, duty drawer), so the photo preview has to
// sit above them or it opens behind (its toolbar is already at 999999 there)
const PREVIEW_Z = 10050;

const extOf = (v) => (typeof v === 'string' ? (v.split('?')[0].split('.').pop() || '').toLowerCase() : '');
const looksLikeFile = (v) => typeof v === 'string' && v.includes('.') && FileFormatTypes.includes(extOf(v));

const isBlank = (v) => {
    if (v === undefined || v === null) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.filter((x) => !isBlank(x)).length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
};

const humanizeKey = (key) => {
    if (KNOWN_KEYS[key]) return KNOWN_KEYS[key];
    if (ID_RE.test(key)) return 'Additional answer';
    const words = String(key).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Answer';
};

const formatDate = (s) => {
    const str = String(s).trim();
    if (ISO_RE.test(str)) return dayjs(str).format('D MMM YYYY');
    const uk = str.match(UK_DATE_RE);
    if (uk) {
        const d = dayjs(`${uk[3]}-${uk[2].padStart(2, '0')}-${uk[1].padStart(2, '0')}`);
        if (d.isValid()) return d.format('D MMM YYYY');
    }
    return str;
};
const formatDateTime = (s) => {
    const d = dayjs(s);
    return d.isValid() ? d.format('D MMM YYYY, HH:mm') : String(s);
};

// keys a multi-photo "file" field stores its photos under: `${instanceId}_photo_0`, _1, ...
const photoKeysFor = (detail, instanceId) => {
    if (!instanceId) return [];
    const prefix = `${instanceId}_photo`;
    const num = (k) => {
        const n = parseInt(k.slice(prefix.length).replace(/\D/g, ''), 10);
        return Number.isNaN(n) ? 0 : n;
    };
    return Object.keys(detail).filter((k) => k.startsWith(prefix)).sort((a, b) => num(a) - num(b));
};

// one answer -> how its ANSWER cell reads: {kind, text?, yes?, value?}
const classify = (type, value, url) => {
    if (type === 'signature') return url ? { kind: 'signature' } : (isBlank(value) ? { kind: 'empty' } : { kind: 'text', text: String(value) });
    if (isBlank(value)) return { kind: 'empty' };
    if (typeof value === 'boolean') return { kind: 'yn', yes: value };
    if (Array.isArray(value)) {
        const items = value.filter((x) => !isBlank(x));
        if (items.every((x) => typeof x !== 'object')) return { kind: 'text', text: items.map(String).join(', ') };
        return { kind: 'object' };
    }
    if (typeof value === 'object') {
        const lat = value.lat ?? value.latitude;
        const lng = value.lng ?? value.lon ?? value.longitude;
        if (lat !== undefined && lng !== undefined) return { kind: 'location', lat, lng };
        return { kind: 'object' };
    }
    const str = String(value).trim();
    const yn = normalizeYesNo(str);
    if (['yes', 'no', 'true', 'false'].includes(yn)) return { kind: 'yn', yes: yn === 'yes' || yn === 'true' };
    if (MULTI_TYPES.has(type)) return { kind: 'text', text: str.split(',').map((x) => x.trim()).filter(Boolean).join(', ') };
    if (type === 'date') return { kind: 'text', text: formatDate(str) };
    if (type === 'time') return { kind: 'text', text: ISO_RE.test(str) && str.includes('T') ? dayjs(str).format('HH:mm') : str };
    if (!TEXT_TYPES.has(type) && url && looksLikeFile(str)) return { kind: 'files', files: [{ url, ext: extOf(str) }] };
    if (!TEXT_TYPES.has(type) && ISO_RE.test(str)) return { kind: 'text', text: str.includes('T') ? formatDateTime(str) : formatDate(str) };
    if (/^https?:\/\/\S+$/.test(str)) return { kind: 'link', text: str };
    return { kind: 'text', text: str, long: type === 'multilineText' || str.length > 120 || str.includes('\n') };
};

// reportDetail + the template's sections -> [{title, rows:[{id, question, answer}]}]
const buildSections = (detail, filesURL, sections) => {
    const fieldState = mapReportDetailToFieldState(detail, sections);
    const keyFor = (item) => (
        item?.instanceId !== undefined && detail[item.instanceId] !== undefined ? item.instanceId : legacyKeyForField(item)
    );

    // every key any template field owns - hidden ones included, so a hidden field's
    // leftover answer never resurfaces under "Additional details"
    const used = new Set();
    sections.flatMap((s) => s?.fields || []).forEach((item) => {
        used.add(keyFor(item));
        if (item?.instanceId) used.add(item.instanceId);
        photoKeysFor(detail, item?.instanceId).forEach((k) => used.add(k));
    });

    // a file answer: its photos show in the answer cell, anything else is a link
    const fileAnswer = (question, sources) => {
        const photos = [];
        const files = [];
        sources.forEach((s, i) => {
            const url = s.key ? filesURL[s.key] : null;
            if (IMAGE_EXT.has(extOf(s.value)) && url) photos.push({ url, caption: sources.length > 1 ? `${question} ${i + 1}` : question });
            else files.push({ url, ext: extOf(s.value) });
        });
        return { kind: 'files', photos, files };
    };

    const built = sections.map((s) => {
        const section = { title: s?.sectionName || 'Details', rows: [] };
        (s?.fields || []).filter((item) => isFieldVisible(item, fieldState)).forEach((item, i) => {
            const question = item?.label || 'Untitled question';
            const id = item?.instanceId || item?._id || `${s?.groupId}-${i}`;
            if (item?.fieldType === 'file') {
                let sources = photoKeysFor(detail, item.instanceId).map((k) => ({ key: k, value: detail[k] }));
                if (!sources.length) {
                    const k = keyFor(item);
                    const v = detail[k];
                    if (Array.isArray(v)) sources = v.map((x) => ({ key: null, value: x }));
                    else if (!isBlank(v)) sources = [{ key: k, value: v }];
                }
                sources = sources.filter((x) => typeof x.value === 'string' && x.value.trim());
                // a photo question nobody answered is left out (only photos - any other
                // unanswered question still shows as "Not answered")
                if (sources.length) section.rows.push({ id, question, answer: fileAnswer(question, sources) });
                return;
            }
            const key = keyFor(item);
            section.rows.push({ id, question, value: detail[key], url: filesURL[key], answer: classify(item?.fieldType, detail[key], filesURL[key]) });
        });
        return section;
    }).filter((s) => s.rows.length);

    const extra = { title: built.length ? 'Additional details' : 'Report details', rows: [] };
    Object.keys(detail).filter((k) => !used.has(k) && !HEADER_KEYS.has(k) && !isBlank(detail[k])).forEach((k) => {
        const value = detail[k];
        const question = PHOTO_SUFFIX_RE.test(k) ? 'Photo' : humanizeKey(k);
        if (filesURL[k] && looksLikeFile(value)) {
            extra.rows.push({ id: k, question, answer: fileAnswer(question, [{ key: k, value }]) });
            return;
        }
        extra.rows.push({ id: k, question, value, answer: classify(undefined, value, null) });
    });
    if (extra.rows.length) built.push(extra);
    return built;
};

const ObjectValue = ({ value }) => {
    if (Array.isArray(value)) return <div>{value.map((v, i) => <ObjectValue key={i} value={v} />)}</div>;
    if (value && typeof value === 'object') {
        return (
            <dl className="gmr-kv">
                {Object.entries(value).filter(([, v]) => !isBlank(v)).map(([k, v]) => (
                    <React.Fragment key={k}>
                        <dt>{humanizeKey(k)}</dt>
                        <dd>{typeof v === 'object' ? <ObjectValue value={v} /> : String(v)}</dd>
                    </React.Fragment>
                ))}
            </dl>
        );
    }
    return <span>{String(value)}</span>;
};

const Answer = ({ row }) => {
    const a = row.answer;
    switch (a.kind) {
        case 'empty':
            return <span className="gmr-missing">Not answered</span>;
        case 'yn':
            return <span>{a.yes ? 'Yes' : 'No'}</span>;
        case 'signature':
            return <div className="gmr-signature"><img src={row.url} alt={row.question} /></div>;
        case 'files':
            return (
                <>
                    {a.photos?.length > 0 && (
                        <Image.PreviewGroup preview={{ zIndex: PREVIEW_Z }}>
                            <div className="gmr-thumbs">
                                {a.photos.map((p, i) => (
                                    <div key={i} className="gmr-thumb"><Image src={p.url} alt={p.caption} /></div>
                                ))}
                            </div>
                        </Image.PreviewGroup>
                    )}
                    {(a.files || []).map((f, i) => (
                        <div key={i}>
                            {f.url ? <a href={f.url} target="_blank" rel="noreferrer">Open {f.ext.toUpperCase() || 'file'}</a> : `${f.ext.toUpperCase() || 'File'} unavailable`}
                        </div>
                    ))}
                </>
            );
        case 'location':
            return <a href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer">{Number(a.lat).toFixed(5)}, {Number(a.lng).toFixed(5)}</a>;
        case 'object':
            return <ObjectValue value={row.value} />;
        case 'link':
            return <a href={a.text} target="_blank" rel="noreferrer">{a.text}</a>;
        default:
            return <span>{a.text}</span>;
    }
};

const Section = ({ section, index }) => {
    const answered = section.rows.filter((r) => r.answer.kind !== 'empty').length;
    const untouched = answered === 0;
    return (
        <section className="gmr-section">
            <div className="gmr-heading">
                <h2>{index + 1}.&nbsp; {section.title}</h2>
                <span className="gmr-heading-note">{untouched ? 'Not completed' : `${answered} of ${section.rows.length} answered`}</span>
            </div>

            {untouched ? (
                <p className="gmr-untouched">
                    No answers were recorded for the {section.rows.length} question{section.rows.length === 1 ? '' : 's'} in this section.
                </p>
            ) : (
                <table className="gmr-table">
                    <thead><tr><th>Question</th><th>Answer</th></tr></thead>
                    <tbody>
                        {section.rows.map((row) => (
                            <tr key={row.id} className={row.answer.kind === 'files' && row.answer.photos?.length ? 'gmr-photo-row' : undefined}>
                                <td className="gmr-q">
                                    {row.question}
                                    {row.answer.photos?.length > 1 && <div className="gmr-photo-count">{row.answer.photos.length} photos</div>}
                                </td>
                                <td className={`gmr-a${row.answer.long ? ' long' : ''}`}><Answer row={row} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

        </section>
    );
};

const mapsUrl = (l) => `https://www.google.com/maps?q=${l.lat},${l.lng}`;

// one table in the answers' style - EVENT | TIME | WHERE | MAP, a small map per row
// (a duty's Clock In / this report's check call / Clock Out, or a mobile service's route)
const LocationSection = ({ location }) => {
    const rows = location.route
        ? [{ title: 'Route', time: location.route.time || '—', lines: [{ text: location.route.detail }], map: location.route.map, link: location.route.finish }]
        : location.places.map((p) => ({
            title: p.title,
            time: p.time || '—',
            lines: [
                ...(p.geofence ? [{ text: p.geofence.text, cls: `gmr-geo ${p.geofence.status}` }] : []),
                { text: `${Number(p.location.lat).toFixed(5)}, ${Number(p.location.lng).toFixed(5)}`, link: p.location },
            ],
            map: p.map,
            link: p.location,
        }));
    return (
        <section className="gmr-section">
            <div className="gmr-heading">
                <h2>Location</h2>
                <span className="gmr-heading-note">recorded by the WFC360 app</span>
            </div>
            <table className="gmr-table gmr-loc">
                <thead><tr><th>Event</th><th>Time</th><th>Where</th><th>Map</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.title}>
                            <td className="gmr-a">{r.title}</td>
                            <td>{r.time}</td>
                            <td className="gmr-loc-where">
                                {r.lines.map((l, i) => (
                                    <div key={i} className={l.cls}>
                                        {l.link ? <a href={mapsUrl(l.link)} target="_blank" rel="noreferrer">{l.text}</a> : l.text}
                                    </div>
                                ))}
                            </td>
                            <td>
                                <a href={r.link ? mapsUrl(r.link) : undefined} target="_blank" rel="noreferrer">
                                    {r.map ? <img className="gmr-map" src={r.map} alt={`Map - ${r.title}`} /> : <div className="gmr-map gmr-map-empty">Map unavailable</div>}
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {location.attribution && <div className="gmr-attribution">{location.attribution}</div>}
        </section>
    );
};

const one = (v) => (Array.isArray(v) ? v[0] : v);

export const GenericMobileReport = ({ checkCallId = "", jobId = null, templateId = "690b6259851a2cf51d57bb89" }) => {

    const [processingPdf, setPdfProcessing] = useState(false);
    const param = useParams();
    const activeJobId = param?.jobId || jobId;
    const activeTemplateId = param?.templateId || templateId;

    const { data, loading } = useQuery(getReportTemplateById, {
        variables: { templateId: activeTemplateId },
        fetchPolicy: 'network-only',
    });

    const { data: reportData, loading: reportDataLoading } = useQuery(getScheduleReportByJobIdQuery, {
        variables: { jobId: activeJobId, checkCallId },
        fetchPolicy: 'network-only',
    });

    const { data: scheduledJobData, loading: scheduledJobLoading } = useQuery(getScheduledJobById, {
        variables: { jobId: activeJobId },
        skip: !activeJobId,
        fetchPolicy: 'network-only',
    });

    const { data: mobileResponseData, loading: mobileResponseLoading } = useQuery(getMobileResponseByIdQuery, {
        variables: { responseId: activeJobId },
        skip: !activeJobId,
        fetchPolicy: 'network-only',
    });

    // where the app recorded the officer - the proof of attendance's own data and maps
    const source = scheduledJobData?.getScheduledJobById?.response ? 'schedule'
        : mobileResponseData?.getMobileResponseById?.response ? 'mobileService' : null;
    const { data: proofData } = useQuery(getDutyProofQuery, {
        variables: { source, id: activeJobId },
        skip: !source || !activeJobId,
        fetchPolicy: 'network-only',
    });

    // a schedule duty or a mobile service - whichever this job is
    const jobDetail = scheduledJobData?.getScheduledJobById?.response || mobileResponseData?.getMobileResponseById?.response || null;
    const report = reportData?.getScheduleReportByJobId?.response;
    const template = data?.getReportTemplateById?.response?.template;

    // the signed-in user's company - named on reports of duties a supplier holds
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer) || {};
    const tenant = authenticatedUserDetail?.tenantDetail?.[0];

    const sections = useMemo(() => buildSections(
        report?.reportDetail || {},
        report?.filesURL || {},
        template?.reportFields || [],
    ), [report, template]);

    const header = useMemo(() => {
        const detail = report?.reportDetail || {};
        const job = jobDetail || {};
        const site = one(job.site) || one(job.siteDetail);
        const employee = one(job.assignedEmployeeDetail) || one(job.employeeDetail);
        const supplier = one(job.assignedAgencyDetail) || one(job.supplierDetail);
        const customer = one(job.customerDetail);

        const street = String(site?.metadata?.address || '').trim();
        const postcode = String(site?.postcode || '').trim();
        const address = (street && postcode && !street.includes(postcode) ? `${street}, ${postcode}` : street || postcode)
            || (typeof detail.siteaddress === 'string' ? detail.siteaddress : null);

        let dutyDate = null;
        let dutyTime = null;
        if (job.firstDate) {
            dutyDate = dayjs(job.firstDate).format('D MMM YYYY');
            dutyTime = job.shiftStartTime ? `${dayjs(job.firstDate).format('ddd')} · ${job.shiftStartTime}${job.shiftEndTime ? `–${job.shiftEndTime}` : ''}` : null;
        } else if (job.startTime || job.startAt) {
            const d = dayjs(job.startTime || job.startAt);
            if (d.isValid()) { dutyDate = d.format('D MMM YYYY'); dutyTime = d.format('ddd · HH:mm'); }
        }

        const submitted = report?.submittedAt || report?.updatedAt || report?.createdAt;
        const dutyType = one(job.dutyTypeDetail)?.name || one(job.dutyDetail)?.name;
        const employeeName = employee ? `${employee.firstname || ''} ${employee.lastname || ''}`.replace(/\s+/g, ' ').trim() : null;
        // a duty held by a supplier is reported under our own (the tenant's) name - the
        // supplier is never named on the report; the officer goes under it
        const tenantName = String(tenant?.legalname || tenant?.name || '').trim() || null;
        const supplierName = supplier ? tenantName : null;

        const reportName = template?.templatename || dutyType || (typeof detail.jobtype === 'string' && detail.jobtype) || 'Report';
        // the report's name on top; the duty type and customer - what the report is for -
        // as the title (the report's name takes over when neither is known)
        const bandTitle = [dutyType, customer?.customername].filter(Boolean).join(' · ') || reportName;
        return {
            title: reportName,
            bandTitle,
            eyebrow: bandTitle === reportName ? 'Report' : reportName,
            subtitle: [site?.sitename || (typeof detail.sitename === 'string' ? detail.sitename : null), address].filter(Boolean).join('  ·  '),
            officerName: supplierName || employeeName,
            officerSub: supplierName && employeeName ? `Officer: ${employeeName}` : null,
            submitted: submitted ? formatDateTime(submitted) : null,
            dutyDate,
            dutyTime,
        };
    }, [report, jobDetail, template, tenant]);

    // Clock In / this report's check call / Clock Out for a duty, the recorded route for a mobile service
    const location = useMemo(() => {
        const proof = proofData?.getDutyProof?.response;
        if (!proof) return null;
        const hm = (v) => (v ? dayjs(v).format('HH:mm') : null);
        if (source === 'mobileService') {
            if (!proof.route?.map) return null;
            const at = (type) => (proof.events || []).find((e) => e.type === type)?.at;
            const time = [...new Set([hm(at('Started')), hm(at('Finished'))].filter(Boolean))].join('–') || null;
            const detail = proof.route.points
                ? `${proof.route.points} point${proof.route.points === 1 ? '' : 's'} recorded by the officer's phone`
                : "Route recorded by the officer's phone";
            return { route: { map: proof.route.map, time, detail, finish: proof.route.finish }, places: [], attribution: proof.mapAttribution };
        }
        const located = (proof.events || []).filter((e) => e.location && e.at);
        const reportCall = checkCallId ? located.find((e) => String(e._id) === String(checkCallId)) : null;
        const places = [['Clock in', located.find((e) => e.type === 'Clock In')], ['Report completed', reportCall], ['Clock out', located.find((e) => e.type === 'Clock Out')]]
            .filter(([, e]) => e)
            .map(([title, e]) => ({ title, time: hm(e.at), location: e.location, map: e.map, geofence: e.geofence }));
        return places.length ? { route: null, places, attribution: proof.mapAttribution } : null;
    }, [proofData, source, checkCallId]);

    const totals = useMemo(() => {
        const rows = sections.flatMap((s) => s.rows);
        const answered = rows.filter((r) => r.answer.kind !== 'empty').length;
        return {
            questions: rows.length,
            answered,
            missing: rows.length - answered,
            photos: rows.reduce((n, r) => n + (r.answer.photos?.length || 0), 0),
        };
    }, [sections]);

    // the PDF is drawn by wfc-server in the same layout (routes/formReport.route.ts),
    // signed in with the web session cookie
    const downloadPdf = async () => {
        setPdfProcessing(true);
        try {
            await downloadReportPdf({ jobId: activeJobId, checkCallId, templateId: activeTemplateId, filename: header.title });
            message.success("Report downloaded");
        } catch (err) {
            console.error(err);
            message.error(err?.message || "Couldn't create the PDF. Please try again.");
        } finally {
            setPdfProcessing(false);
        }
    };

    if (reportDataLoading || loading || scheduledJobLoading || mobileResponseLoading) {
        return <div className="gmr-loading"><Spinner /></div>;
    }

    return (
        <div className="gmr">
            <div id="pdfexportBtn" className="gmr-toolbar">
                <button className="drawer-filled-btn" disabled={processingPdf} onClick={downloadPdf}>
                    {processingPdf ? <Spinner /> : <FontAwesomeIcon icon={faDownload} />}
                    {processingPdf ? 'Preparing PDF…' : 'Download PDF'}
                </button>
            </div>

            <div className="gmr-page" id="pdfContent">
                <header className="gmr-band">
                    <div style={{ minWidth: 0 }}>
                        <div className="gmr-eyebrow">{header.eyebrow}</div>
                        <h1 className="gmr-title">{header.bandTitle}</h1>
                        {header.subtitle && <div className="gmr-subtitle">{header.subtitle}</div>}
                    </div>
                </header>

                <div className="gmr-body">
                    <div className="gmr-officer">
                        <div style={{ minWidth: 0 }}>
                            <div className="gmr-kicker">Completed by</div>
                            <div className="gmr-officer-name">{header.officerName || 'Not recorded'}</div>
                            {header.officerSub && <div className="gmr-card-sub">{header.officerSub}</div>}
                        </div>
                        {header.submitted && (
                            <div className="gmr-officer-right">
                                <div className="gmr-kicker">{report?.isSubmitted ? 'Submitted' : 'Last saved'}</div>
                                <div className="gmr-strong">{header.submitted}</div>
                            </div>
                        )}
                    </div>

                    <div className="gmr-cards">
                        <div className="gmr-card">
                            <div className="gmr-kicker">{header.dutyDate ? 'Duty' : 'Report date'}</div>
                            <div className="gmr-card-value">{header.dutyDate || (header.submitted || '—').split(',')[0]}</div>
                            <div className="gmr-card-sub">{header.dutyDate ? header.dutyTime : 'duty details unavailable'}</div>
                        </div>
                        <div className="gmr-card">
                            <div className="gmr-kicker">Questions answered</div>
                            <div className="gmr-card-value">{totals.answered} of {totals.questions}</div>
                            <div className="gmr-card-sub">
                                {totals.missing ? `${Math.round((totals.answered / Math.max(totals.questions, 1)) * 100)}% complete` : 'All answered'}
                            </div>
                        </div>
                        <div className="gmr-card">
                            <div className="gmr-kicker">Not answered</div>
                            <div className={`gmr-card-value${totals.missing ? ' warn' : ''}`}>{totals.missing}</div>
                            <div className="gmr-card-sub">{totals.missing ? 'see the sections below' : 'Nothing missing'}</div>
                        </div>
                        <div className="gmr-card">
                            <div className="gmr-kicker">Photos</div>
                            <div className="gmr-card-value">{totals.photos}</div>
                            <div className="gmr-card-sub">{totals.photos ? 'with their answers' : 'None attached'}</div>
                        </div>
                    </div>

                    {sections.length > 0
                        ? sections.map((section, i) => <Section key={`${section.title}-${i}`} section={section} index={i} />)
                        : <div className="gmr-empty-state">There are no answers in this report yet.</div>}

                    {/* where the app recorded the officer - supporting evidence, after the answers */}
                    {location && <LocationSection location={location} />}

                    <footer className="gmr-footer">
                        <span>End of report</span>
                        {header.submitted && <span>{report?.isSubmitted ? 'Submitted' : 'Last saved'} {header.submitted}</span>}
                    </footer>
                </div>
            </div>
        </div>
    );
};
